import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp, useClinicCtx, isCloud } from '@/app/state';
import { Button, Card, Field, Input, Modal, Pill, cx } from '@/components/ui';
import { inviteStaff, listStaff, setMemberStatus, type StaffRow } from './repo';
import type { Role } from '@/app/state';

const ROLES: Role[] = ['owner', 'doctor', 'nurse', 'receptionist', 'cashier', 'pharmacist'];

export function StaffCard() {
  const { t, i18n } = useTranslation();
  const my = i18n.language === 'my';
  const ctx = useClinicCtx();
  const { session } = useApp();
  const [rows, setRows] = useState<StaffRow[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [f, setF] = useState({ email: '', name: '', name_my: '', roles: ['receptionist'] as Role[] });
  const [version, setVersion] = useState(0);

  useEffect(() => { void listStaff(ctx.db, ctx.clinicId).then(setRows); }, [ctx.db, ctx.clinicId, version]);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await inviteStaff({ clinicId: ctx.clinicId, email: f.email, displayName: f.name, displayNameMy: f.name_my || null, roles: f.roles });
      setDone(f.email);
      setOpen(false);
      setF({ email: '', name: '', name_my: '', roles: ['receptionist'] });
      setVersion((v) => v + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('staff.failed'));
    } finally { setBusy(false); }
  }

  const cloud = isCloud && session?.mode === 'cloud';
  return (
    <Card>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-lg font-bold">{t('staff.title')}</h2>
        <Button onClick={() => setOpen(true)} disabled={!cloud} title={cloud ? undefined : t('staff.cloudOnly')}>＋ {t('staff.invite')}</Button>
      </div>
      {done && <p className="mb-2 text-sm text-accent-ink">{t('staff.sent', { email: done })}</p>}
      <ul className="divide-y divide-rule text-sm">
        {rows.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <div>
              <b>{my ? (r.display_name_my ?? r.display_name) : r.display_name}</b>
              <div className="flex flex-wrap gap-1 pt-0.5">
                {(r.roles ?? '').split(',').filter(Boolean).map((role) => <Pill key={role}>{t(`roles.${role}`)}</Pill>)}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Pill tone={r.has_key > 0 ? 'accent' : 'warn'}>{r.has_key > 0 ? t('staff.keyHeld') : t('staff.keyPending')}</Pill>
              <Pill tone={r.status === 'active' ? 'neutral' : 'danger'}>{t(`staff.${r.status}`)}</Pill>
              {r.user_id !== session?.userId && r.status === 'active' && (
                <button className="text-xs text-ink-2 underline hover:text-danger"
                  onClick={async () => { await setMemberStatus(ctx.db, r.id, 'inactive', ctx.userId); setVersion((v) => v + 1); }}>{t('staff.remove')}</button>
              )}
            </div>
          </li>
        ))}
      </ul>

      <Modal open={open} onClose={() => setOpen(false)} title={t('staff.invite')}>
        <form onSubmit={send} className="grid gap-3">
          <Field label={t('staff.email')}><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required autoFocus /></Field>
          <Field label={t('staff.name')}><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></Field>
          <Field label={`${t('staff.name')} (${my ? 'အင်္ဂလိပ်' : 'Burmese'})`}><Input className="font-my" value={f.name_my} onChange={(e) => setF({ ...f, name_my: e.target.value })} /></Field>
          <Field label={t('staff.roles')}>
            <div className="flex flex-wrap gap-1.5">
              {ROLES.map((role) => (
                <button type="button" key={role}
                  onClick={() => setF({ ...f, roles: f.roles.includes(role) ? f.roles.filter((x) => x !== role) : [...f.roles, role] })}
                  className={cx('rounded-full border px-3 py-1.5 text-sm font-semibold', f.roles.includes(role) ? 'border-accent bg-accent-soft text-accent-ink' : 'border-rule bg-surface text-ink-2')}>
                  {t(`roles.${role}`)}
                </button>
              ))}
            </div>
          </Field>
          <p className="m-0 text-sm text-muted">{t('staff.hint')}</p>
          {error && <p className="m-0 text-sm text-danger">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button type="submit" variant="primary" disabled={busy || f.roles.length === 0}>{t('staff.send')}</Button>
          </div>
        </form>
      </Modal>
    </Card>
  );
}
